import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Building2, Mail, Lock,
  CheckCircle, Package, Check, Eye, EyeOff
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import FloatingParticles from '../components/FloatingParticles';
import { API_BASE, API } from '../../lib/api';


const AVAILABLE_MODULES = [
  { id: 'finance', name: 'Finance & Accounts', category: 'Core', color: '#3DD56D' },
  { id: 'hr', name: 'Human Resources', category: 'Core', color: '#4CC3FF' },
  { id: 'inventory', name: 'Inventory', category: 'Core', color: '#F5C451' },
  { id: 'pos', name: 'Point of Sale', category: 'Core', color: '#FF8C42' },
  { id: 'crm', name: 'CRM & Sales', category: 'Sales', color: '#A78BFA' },
  { id: 'analytics', name: 'Analytics', category: 'Intelligence', color: '#3DD56D' },
  { id: 'procurement', name: 'Procurement', category: 'Operations', color: '#F5C451' },
  { id: 'payroll', name: 'Payroll', category: 'Core', color: '#4CC3FF' },
  { id: 'audit', name: 'Audit & Compliance', category: 'Compliance', color: '#FF5A6A' },
  { id: 'settings', name: 'System Settings', category: 'Admin', color: '#7A7A7A' },
];

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];
const INDUSTRIES = ['Retail', 'E-Commerce', 'Wholesale', 'Fashion', 'Electronics', 'Grocery', 'Pharmacy', 'Other'];

const STEPS = [
  { id: 1, label: 'Company' },
  { id: 2, label: 'Admin' },
  { id: 3, label: 'Modules' },
];

export default function SignupPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const ERP_LOGIN_URL = process.env.REACT_APP_ERP_URL
    ? `${process.env.REACT_APP_ERP_URL}/login`
    : '/login';
  const goToLogin = () => { window.location.href = ERP_LOGIN_URL; };
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [refId, setRefId] = useState('');
  const [loading, setLoading] = useState(false);

  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);

  const [form, setForm] = useState({
    company_name: '',
    size: '',
    industry: '',
    branch: '',
    admin_email: '',
    password: '',
    confirm_password: '',
    modules: ['finance', 'inventory', 'pos'],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleModule = (id) => {
    setForm(f => ({
      ...f,
      modules: f.modules.includes(id)
        ? f.modules.filter(m => m !== id)
        : [...f.modules, id],
    }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.company_name.trim()) { toast.error('Company name is required'); return false; }
      if (!form.industry) { toast.error('Please select an industry'); return false; }
    }
    if (step === 2) {
      if (!form.admin_email.trim() || !form.admin_email.includes('@')) {
        toast.error('Please enter a valid email'); return false;
      }
      if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
      if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return false; }
    }
    if (step === 3) {
      if (form.modules.length === 0) { toast.error('Please select at least one module'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, {
        company_name: form.company_name,
        admin_email: form.admin_email,
        password: form.password,
        branch: form.branch,
        modules: form.modules,
      });
      setRefId(data.reference);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark:bg-[#080808] bg-slate-50 flex flex-col relative overflow-hidden">
      {isDark && <FloatingParticles />}

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5 dark:border-b dark:border-white/[0.06] border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            onClick={goToLogin}
            className="w-9 h-9 rounded-xl dark:bg-white/[0.05] bg-white dark:border-white/[0.09] border-gray-200 border flex items-center justify-center dark:text-neutral-400 text-gray-500 dark:hover:text-white hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <RetailCliqLogo size="sm" />
        </div>
        <ThemeToggle />
      </div>

      {/* Main */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-5">
        <div className="w-full max-w-lg">

          {!done ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Title */}
              <div className="text-center mb-8">
                <h1
                  className="text-2xl font-bold dark:text-white text-gray-900"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Request Access
                </h1>
                <p className="dark:text-neutral-400 text-gray-500 text-sm mt-1.5">
                  Set up your company on RetailCliQ ERP
                </p>
              </div>

              {/* Step indicators */}
              <div className="flex items-center justify-center mb-8">
                {STEPS.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold transition-all ${
                          step > s.id
                            ? 'bg-[#3DD56D] text-black'
                            : step === s.id
                            ? 'bg-[#3DD56D]/10 border-2 border-[#3DD56D] text-[#3DD56D]'
                            : 'dark:bg-white/[0.04] bg-gray-100 dark:border-white/[0.1] border-gray-200 border-2 dark:text-neutral-500 text-gray-400'
                        }`}
                      >
                        {step > s.id ? <Check size={16} /> : s.id}
                      </div>
                      <span
                        className={`text-xs mt-1.5 font-medium ${
                          step >= s.id ? 'dark:text-white text-gray-900' : 'dark:text-neutral-500 text-gray-400'
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`h-0.5 w-16 mx-3 mt-[-14px] rounded-full transition-all ${
                          step > s.id ? 'bg-[#3DD56D]' : 'dark:bg-white/[0.08] bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Card */}
              <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border dark:shadow-[0_24px_60px_rgba(0,0,0,0.5)] shadow-xl rounded-2xl p-7">
                <AnimatePresence mode="wait">
                  {/* STEP 1 - Company */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center gap-2 mb-5">
                        <Building2 size={18} className="text-[#3DD56D]" />
                        <h2 className="font-semibold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          Company Details
                        </h2>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm dark:text-neutral-300 text-gray-700">Company Name *</Label>
                        <Input
                          placeholder="RetailCliQ Corp"
                          value={form.company_name}
                          onChange={e => set('company_name', e.target.value)}
                          className="h-11 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-sm dark:text-neutral-300 text-gray-700">Industry *</Label>
                          <select
                            value={form.industry}
                            onChange={e => set('industry', e.target.value)}
                            className="w-full h-11 px-3 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border border-gray-200 dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#3DD56D] text-sm appearance-none"
                          >
                            <option value="">Select...</option>
                            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm dark:text-neutral-300 text-gray-700">Company Size</Label>
                          <select
                            value={form.size}
                            onChange={e => set('size', e.target.value)}
                            className="w-full h-11 px-3 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border border-gray-200 dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#3DD56D] text-sm appearance-none"
                          >
                            <option value="">Select...</option>
                            {COMPANY_SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm dark:text-neutral-300 text-gray-700">Primary Branch / Location</Label>
                        <Input
                          placeholder="e.g., Mumbai HQ"
                          value={form.branch}
                          onChange={e => set('branch', e.target.value)}
                          className="h-11 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D]"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 2 - Admin */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center gap-2 mb-5">
                        <Mail size={18} className="text-[#3DD56D]" />
                        <h2 className="font-semibold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          Administrator Account
                        </h2>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm dark:text-neutral-300 text-gray-700">Admin Email *</Label>
                        <div className="relative">
                          <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" />
                          <Input
                            type="email"
                            placeholder="admin@yourcompany.com"
                            value={form.admin_email}
                            onChange={e => set('admin_email', e.target.value)}
                            className="pl-10 h-11 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D]"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm dark:text-neutral-300 text-gray-700">Password *</Label>
                        <div className="relative">
                          <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" />
                          <Input
                            type={showPass ? 'text' : 'password'}
                            placeholder="Min. 6 characters"
                            value={form.password}
                            onChange={e => set('password', e.target.value)}
                            className="pl-10 pr-10 h-11 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D]"
                          />
                          <button type="button" onClick={() => setShowPass(v => !v)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400">
                            {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm dark:text-neutral-300 text-gray-700">Confirm Password *</Label>
                        <div className="relative">
                          <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" />
                          <Input
                            type={showConf ? 'text' : 'password'}
                            placeholder="Repeat password"
                            value={form.confirm_password}
                            onChange={e => set('confirm_password', e.target.value)}
                            className="pl-10 pr-10 h-11 rounded-xl dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.1] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D]"
                          />
                          <button type="button" onClick={() => setShowConf(v => !v)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400">
                            {showConf ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* STEP 3 - Modules */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex items-center gap-2 mb-5">
                        <Package size={18} className="text-[#3DD56D]" />
                        <h2 className="font-semibold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          Select Modules
                        </h2>
                      </div>
                      <p className="dark:text-neutral-400 text-gray-500 text-xs mb-4">
                        Choose the modules for your company. You can change these later.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {AVAILABLE_MODULES.map(mod => (
                          <button
                            key={mod.id}
                            type="button"
                            onClick={() => toggleModule(mod.id)}
                            className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all ${
                              form.modules.includes(mod.id)
                                ? 'border-[#3DD56D]/50 bg-[#3DD56D]/[0.08]'
                                : 'dark:bg-white/[0.03] bg-gray-50 dark:border-white/[0.08] border-gray-200 dark:hover:bg-white/[0.06] hover:bg-gray-100'
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                                form.modules.includes(mod.id)
                                  ? 'bg-[#3DD56D]'
                                  : 'dark:bg-white/[0.08] bg-gray-200'
                              }`}
                            >
                              {form.modules.includes(mod.id) && <Check size={12} className="text-black" />}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-xs font-medium truncate ${
                                form.modules.includes(mod.id) ? 'text-[#3DD56D]' : 'dark:text-neutral-300 text-gray-700'
                              }`}>
                                {mod.name}
                              </p>
                              <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{mod.category}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                      <p className="dark:text-neutral-600 text-gray-400 text-xs mt-3">
                        {form.modules.length} module{form.modules.length !== 1 ? 's' : ''} selected
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation buttons */}
                <div className="flex gap-3 mt-7">
                  {step > 1 && (
                    <Button
                      variant="outline"
                      onClick={back}
                      className="flex-1 h-11 rounded-xl dark:border-white/[0.1] dark:hover:bg-white/[0.05] text-sm"
                    >
                      <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                  )}
                  {step < 3 ? (
                    <Button
                      onClick={next}
                      className="flex-1 h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold text-sm"
                    >
                      Next <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold text-sm disabled:opacity-50"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                          Submitting...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Submit Request <ArrowRight size={14} />
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-center dark:text-neutral-600 text-gray-400 text-xs mt-4">
                Already have access?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); goToLogin(); }} className="text-[#3DD56D] hover:text-[#5fe88a]">Sign in</a>
              </p>
            </motion.div>
          ) : (
            /* SUCCESS STATE */
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-10 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#3DD56D]/10 border-2 border-[#3DD56D]/25 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={36} className="text-[#3DD56D]" />
              </div>
              <h2
                className="text-xl font-bold dark:text-white text-gray-900 mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Request Submitted!
              </h2>
              <p className="dark:text-neutral-400 text-gray-500 text-sm leading-relaxed mb-6">
                Your access request has been received. Our team will activate your account within 24 hours.
              </p>
              <div className="dark:bg-white/[0.04] bg-gray-50 border dark:border-white/[0.08] border-gray-200 rounded-xl px-5 py-4 mb-6">
                <p className="dark:text-neutral-500 text-gray-500 text-xs mb-1">Reference ID</p>
                <p
                  className="text-[#3DD56D] font-bold text-lg tracking-wider"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {refId}
                </p>
              </div>
              <Button
                data-testid="signup-success-login"
                onClick={goToLogin}
                className="w-full h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold"
              >
                Continue to login <ArrowRight size={12} />
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
